import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import type {
  Process,
  CreateProcessRequest,
  UpdateProcessRequest,
} from '@techgitter/contracts';

interface ProcessDialogProps {
  open: boolean;
  process: Process | null;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (payload: CreateProcessRequest | UpdateProcessRequest) => Promise<void>;
}

const URL_PATTERN = /^https?:\/\/.+/;

export const ProcessDialog: React.FC<ProcessDialogProps> = ({
  open,
  process,
  isSaving,
  error,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isReportableOnEmail, setIsReportableOnEmail] = useState(false);
  const [urlAzure, setUrlAzure] = useState('');
  const [urlInfo, setUrlInfo] = useState('');

  useEffect(() => {
    if (process) {
      setName(process.name ?? '');
      setCategory(process.category ?? '');
      setDescription(process.description ?? '');
      setIsActive(process.isActive);
      setIsReportableOnEmail(process.isReportableOnEmail);
      setUrlAzure(process.urlAzure ?? '');
      setUrlInfo(process.urlInfo ?? '');
    } else {
      setName('');
      setCategory('');
      setDescription('');
      setIsActive(true);
      setIsReportableOnEmail(false);
      setUrlAzure('');
      setUrlInfo('');
    }
  }, [process]);

  const isCreate = process === null;

  const trimmedName = name.trim();
  const trimmedAzure = urlAzure.trim();
  const trimmedInfo = urlInfo.trim();

  const isAzureValid = trimmedAzure === '' || URL_PATTERN.test(trimmedAzure);
  const isInfoValid = trimmedInfo === '' || URL_PATTERN.test(trimmedInfo);

  const isValid = trimmedName.length > 0 && isAzureValid && isInfoValid;

  const isDirty =
    isCreate ||
    trimmedName !== (process.name ?? '') ||
    category.trim() !== (process.category ?? '') ||
    description.trim() !== (process.description ?? '') ||
    isActive !== process.isActive ||
    isReportableOnEmail !== process.isReportableOnEmail ||
    trimmedAzure !== (process.urlAzure ?? '') ||
    trimmedInfo !== (process.urlInfo ?? '');

  const handleSave = async () => {
    if (!isDirty || !isValid) return;

    const payload: CreateProcessRequest = {
      name: trimmedName,
      category: category.trim(),
      description: description.trim(),
      isActive,
      isReportableOnEmail,
      urlAzure: trimmedAzure,
      urlInfo: trimmedInfo,
    };

    await onSave(payload);
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isCreate ? 'Add Process' : 'Edit Process'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
            placeholder="e.g. Sync, Billing, Maintenance"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Azure URL"
            value={urlAzure}
            onChange={(e) => setUrlAzure(e.target.value)}
            fullWidth
            placeholder="https://portal.azure.com/..."
            error={!isAzureValid}
            helperText={!isAzureValid ? 'Must start with http:// or https://' : undefined}
          />
          <TextField
            label="Info URL"
            value={urlInfo}
            onChange={(e) => setUrlInfo(e.target.value)}
            fullWidth
            placeholder="https://docs.example.com/..."
            error={!isInfoValid}
            helperText={!isInfoValid ? 'Must start with http:// or https://' : undefined}
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Active"
          />
          <FormControlLabel
            control={
              <Switch
                checked={isReportableOnEmail}
                onChange={(e) => setIsReportableOnEmail(e.target.checked)}
              />
            }
            label="Reportable on email"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isDirty || !isValid || isSaving}
        >
          {isSaving ? 'Saving...' : isCreate ? 'Create' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
